import type { CampaignAdditionalPaymentResource } from '@metapic-apps/shared'

import type { ProcessableImageType, ProcessableServerImageType } from '@/types/common.types'
import type {
  GroupedTrafficSourceCostType,
  TrafficSourceCostServerType,
  TrafficSourceCostsType,
} from '@/types/trafficSourceCost'

export enum CampaignTypeEnum {
  STANDARD = 'standard',
  STORE_ACCEPT = 'store_accept',
  USER_ACCEPT = 'user_accept',
  SUGGESTION = 'suggestion',
}

export enum CampaignStatusEnum {
  Ended = 'ended',
  Scheduled = 'scheduled',
  Paused = 'paused',
  Active = 'active',
}

export type CampaignServerType = {
  id: number
  type: CampaignTypeEnum
  store_id: number
  status: CampaignStatusEnum
  campaign_text: string
  campaign_title: string
  constraints: {
    current_clicks: number
    current_money: number
    max_clicks: number
    max_money: number
    per_user_limit: boolean
    valid_from: string
    valid_until: string
  }
  additional_payment?: CampaignAdditionalPaymentResource
  store_group_id: number
  todo: string[]
  image?: ProcessableServerImageType
  has_product_seeding: number
  token: string
  traffic_sources_costs: TrafficSourceCostServerType[]
  store_logo: ProcessableServerImageType
  store_name: string
  hashtags?: string[]
}

export type CampaignType = {
  id: number
  advertiserId: number
  title: string
  description: string
  logo?: string
  image?: ProcessableImageType
  hasProductSeeding: boolean
  hasTodoListEnable: boolean
  todoList: { key: number | string; value: string }[]
  startDate: string | null
  endDate: string | null
  hasStartNow: boolean
  hasOpenEnd: boolean
  hashtags: string[]
  maxClicks: number | string
  maxBudget: number | string
  hasIndividualLimitPerUserEnable: boolean
  hasCommissionEnable: boolean
  hasAdditionalPayment: boolean
  additionalPayment: number
  additionalPaymentRevenueInvoice?: number
  groupedTrafficSourceCosts: GroupedTrafficSourceCostType[]
  hasInviteMetapicCreatorsEnable: boolean
  hasApproveCreatorsEnable: boolean // the type of campaign is store_accept
  tagIds?: number[]
  socialMediaIdentifiers?: string[]
  emails?: string[]
  invitedUsers?: InvitedUserType[]
  segments?: CampaignSegmentTarget[]
  type?: CampaignTypeEnum
  token?: string
  advertiserLogo: ProcessableImageType | null
  advertiserName: string
}

export type InvitedUserType = { identifier: string; type: 'instagram' | 'email' }

export type CampaignSegmentTarget = { id: string; name: string }

export type CampaignListItemServerType = {
  id: number
  type: CampaignTypeEnum
  status: CampaignStatusEnum
  created_at: string
  updated_at: string
  campaign_title?: string
  constraints: {
    current_clicks?: number
    current_money?: number
    max_clicks?: number
    max_money?: number
    per_user_limit?: boolean
    valid_from?: string
    valid_until?: string
  }
  has_product_seeding: boolean
  image_url: string
  image?: ProcessableServerImageType
  name: string
  offer_campaign_title: string
  offer_campaign_test: string
  priority: number
  show_for_advertiser: boolean
  traffic_sources_costs?: TrafficSourceCostServerType[]
  store_id: number
  store_logo: ProcessableServerImageType
  store_name: string
  currency: string
  consumed_budget?: {
    amount: number
    currency: string
  }
  budget_limit?: number
  targets?: {
    user_ids: number[]
    store_group_ids: number[]
    user_tag_ids: number[]
    client_ids: number[]
    revenue_tier_ids: number[]
    emails: string[]
    social_media_identifiers: string[]
  }
}

export type CampaignListItemType = {
  id: number
  type: CampaignTypeEnum
  status: CampaignStatusEnum
  internalName: string
  name: string
  trafficSourceCosts?: TrafficSourceCostsType[]
  advertiserLogo?: ProcessableImageType
  advertiserId: number
  advertiserName: string
  advertiserCurrency: string
  currentClicks?: number
  maxClicks?: number
  currentMoney?: number
  maxMoney?: number
  validFrom?: string
  validUntil?: string
  image?: ProcessableImageType
  perUserLimit: boolean
  budgetLimit?: number
  consumedBudget?: {
    amount: number
    currency: string
  }
  targets?: {
    users: number[]
    storeGroups: number[]
    userTags: number[]
    clients: number[]
    revenueTiers: number[]
    emails: string[]
    socialMediaIdentifiers: string[]
  }
}
